import { Engine, Camera, TransformNode } from "babylonjs/index";
import { Scene } from "babylonjs/scene";
import ContentBase from "../core/content.base";
import ContentLincolnCenterBase from "./content.lincolncenter.base";
import { IAssetBase, IAssetTexture } from "@/apps/app.content";
import ContentLincolnCenterOcclusion from "./content.lincolncenter.occlusion";
import ContentLincolnCenterCalibration from "./content.lincolncenter.calibration";
import ContentLincolnCenterArrow from "./content.lincolncenter.arrow";

export type ContentLoadFunc = (assets: IAssetBase[]) => void;

export default class ContentLincolnCenterScene extends ContentLincolnCenterBase {
  protected contents: Map<string, ContentBase> = new Map();
  protected currentState: number | undefined;
  protected canPick: boolean = true;
  protected contentLoadFunc!: ContentLoadFunc;
  protected contentUnloadFunc!: ContentLoadFunc;
  public isSim3D: boolean = false;


  // Lincoln Center specific
  protected contentOcclusion = new ContentLincolnCenterOcclusion();
  protected contentCalibration = new ContentLincolnCenterCalibration();
  protected contentUI = new ContentLincolnCenterArrow();


  public setupCallbacks(loadFunc: ContentLoadFunc, unloadFunc: ContentLoadFunc) {
    this.contentLoadFunc = loadFunc;
    this.contentUnloadFunc = unloadFunc;
  }

  override init(
    engine: Engine,
    scene: Scene,
    camera: Camera,
    rootNode?: TransformNode
  ): void {
    super.init(engine, scene, camera, rootNode);
    this.contentList.forEach((content) => {
      content.init(engine, scene, camera, rootNode)
    });

   

    this.addContent('occlusion', this.contentOcclusion);
    this.addContent('calibration', this.contentCalibration);
    this.addContent('ui', this.contentUI);

    this.contentCalibration.setArrowUI(this.contentUI);


    document.addEventListener('keydown', this.keydown.bind(this));
  }

  //#region Lincoln Content Management

  public async beginCalibrationVisuals() {
    this.contentCalibration.startCalibration();
  }

  public endCalibrationVisuals(): void {
    this.contentCalibration.stopCalibration();
  }

  //#endregion

  //#region General Content Management
  protected addContent(id: string, content: ContentBase, immediateLoad: boolean = false): void {
    this.contents.set(id, content);
    // init
    content.init(this.engine!, this.scene!, this.camera!, this.rootNode!);
    if (immediateLoad) {
      this.contentLoadFunc(content.assets);
    }
  }

  protected getContent(id: string): ContentBase | undefined {
    return this.contents.get(id);
  }

  protected get contentList(): ContentBase[] {
    return Array.from(this.contents.values());
  }

  public override loadAssetComplete(_asset: IAssetBase): void {
    super.loadAssetComplete(_asset);
    Object.values(this.contentList).forEach(content => {
      content.loadAssetComplete(_asset);
    });
  }

  public switchState(state: number) {
    try {
      if (this.currentState === state) {
        return;
      }

      if (this.currentState !== undefined) this.stopState(this.currentState);

      this.startState(state);
      this.currentState = state;
    } catch (e) {
      console.error("State swap error", e);
    }
  }

  protected startState(newState: number) { }
  protected stopState(oldState: number) { }

  override render(): void {
    super.render();


    for (const content of this.contentList) {
      content.render();
    }
  }

  private keydown(event: KeyboardEvent) {
    if (event.key === 'd' || event.key === 'D') {
      if (event.shiftKey) {
        if (this.scene) {
          const debugLayer = this.scene.debugLayer;
          if (debugLayer.isVisible()) {
            debugLayer.hide();
          } else {
            this.scene?.debugLayer.show({
              overlay: false,
              embedMode: true,
            });
          }
        }
      }
    }
  }

  public toggleContentVisibility(id: string, isVisible: boolean) {
    throw Error("Not implemented");
  }

  public dispose() {
    throw Error("Not implemented");
  }

  public start() {
    throw Error("Not implemented");
  }

  public setCanPick(canPick: boolean) {
    this.canPick = canPick;
  }

  public override resize(): void {
    this.contentList.forEach(content => {
      content.resize();
    });
  }

  //#endregion
}
